import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import Rating from "@/components/Rating";
import VLFCard from "@/components/Card";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import {ExerciseAPI} from "@/api/exercise";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {SUCCESSFUL_OPERATION, ERROR_OPERATION} from "@/utils/constants";
import {
	checkRequiredFields,
	getTextField,
	getTextAreaField,
} from "@/utils/functions";

const HandleExercisePage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const [loading, setLoading] = useState(false);
	const [fields, setFields] = useState({});
	const [errors, setErrors] = useState([]);

	const getExercise = async () => {
		setLoading(true);
		try {
			const response = await ExerciseAPI.getById(id);
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getExercise();
		}
	}, [edit]);

	const getRequiredFields = () => {
		return ["name", "exerciseType"];
	};

	const saveExercise = async () => {
		const errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			setLoading(true);

			let body = Object.assign({}, fields);
			body.exerciseType = body.exerciseType || "";
			body.duration = body.duration || 0;
			body.description = body.description || "";

			try {
				const response = edit
					? await ExerciseAPI.update(id, body)
					: await ExerciseAPI.create(body);
				setTimeout(() => {
					navigate(`/exercises/${response?.id || id}`);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveExercise();
	};

	const goBack = () => {
		if (edit) {
			navigate("/exercises/" + id);
		} else {
			navigate("/exercises");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [{label: "Esercizi", path: "/exercises"}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields ? fields.name : "",
				path: "/exercises/" + id,
			},
		);
	}

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin">
				<div className="col-12 padding-small">
					<VLFCard
						title="Informazioni principali"
						content={
							<div className="row">
								<div className="col-12 col-md-6">
									{getTextField({
										...defaultTextField,
										fieldKey: "name",
										label: "Titolo",
										required: true,
									})}
								</div>
								<div className="col-12 col-md-6">
									{getTextField({
										...defaultTextField,
										fieldKey: "exerciseType",
										label: "Tipologia",
										required: true,
									})}
								</div>
								<div className="col-12 col-md-6">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "materials",
										label: "Materiale",
										minRows: 5,
										maxRows: 5,
									})}
								</div>
								<div className="col-12 col-md-6">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "preparation",
										label: "Preparazione",
										minRows: 5,
										maxRows: 5,
									})}
								</div>
								<div className="col-12 col-md-6">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "summary",
										label: "Sommario",
										minRows: 3,
										maxRows: 3,
									})}
								</div>
								<div className="col-12 col-md-6">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "organization",
										label: "Organizzazione",
										minRows: 3,
										maxRows: 3,
									})}
								</div>
								<div className="col-12 col-md-4">
									{getTextField({
										...defaultTextField,
										fieldKey: "duration",
										label: "Tempo di svolgimento (minuti)",
										type: "number",
									})}
								</div>
								<div className="col-12 col-md-4">
									{getTextField({
										...defaultTextField,
										fieldKey: "objectives",
										label: "Obiettivi",
									})}
								</div>
								<div className="col-12 col-md-4">
									{getTextField({
										...defaultTextField,
										fieldKey: "secondaryObjectives",
										label: "Obiettivi secondari",
									})}
								</div>
								<div className="col-12">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "description",
										label: "Descrizione",
										minRows: 10,
										maxRows: 10,
									})}
								</div>
								<div className="col-12">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "rules",
										label: "Regole",
										minRows: 5,
										maxRows: 5,
									})}
								</div>
								<div className="col-12">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "variants",
										label: "Varianti",
										minRows: 5,
										maxRows: 5,
									})}
								</div>
								<div className="col-12">
									{getTextAreaField({
										...defaultTextField,
										fieldKey: "coachingThemes",
										label: "Temi per l'allenatore",
										minRows: 5,
										maxRows: 5,
									})}
								</div>
								<div className="col-12 col-md-6">
									<div className="field-label-container">
										<div className="field-label">{"Difficoltà"}</div>
									</div>
									<Rating
										max={5}
										selectedRating={fields?.difficulty}
										setSelectedRating={(value) => {
											setFields({
												...fields,
												difficulty: value,
											});
										}}
									/>
								</div>
								<div className="col-12 col-md-6">
									{getTextField({
										...defaultTextField,
										fieldKey: "tags",
										label: "Tag",
									})}
								</div>
								<div className="col-12 col-md-6">
									<FormControlLabel
										control={
											<Checkbox
												checked={fields?.basicActivities || false}
												onChange={(event) =>
													setFields({
														...fields,
														basicActivities: event.target.checked,
													})
												}
												inputProps={{"aria-label": "controlled"}}
											/>
										}
										label="Attività di base"
									/>
								</div>
								<div className="col-12 col-md-6">
									<FormControlLabel
										control={
											<Checkbox
												checked={fields?.goalkeepers || false}
												onChange={(event) =>
													setFields({
														...fields,
														goalkeepers: event.target.checked,
													})
												}
												inputProps={{"aria-label": "controlled"}}
											/>
										}
										label="Portieri"
									/>
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
			</div>
		</Page>
	);
};

export default HandleExercisePage;
